import type { GameView } from '../shared/types';

export type AudioCue = 'throw' | 'move' | 'stack' | 'capture' | 'finish' | 'victory' | 'your-turn' | 'card' | 'environment';
export type AudioStatus = 'locked' | 'ready' | 'paused' | 'unsupported';
export interface AudioPreferences { bgm: boolean; sfx: boolean; volume: number }
export const DEFAULT_AUDIO: AudioPreferences = { bgm: false, sfx: false, volume: 0.45 };
export const AUDIO_STORAGE_KEY = 'dalbit:audio:v1';

export function parseAudioPreferences(raw: string | null): AudioPreferences {
  try {
    const parsed: unknown = raw ? JSON.parse(raw) : undefined;
    if (!parsed || typeof parsed !== 'object') return { ...DEFAULT_AUDIO };
    const value = parsed as Partial<AudioPreferences>;
    return {
      bgm: value.bgm === true,
      sfx: value.sfx === true,
      volume: typeof value.volume === 'number' && Number.isFinite(value.volume) ? Math.min(1, Math.max(0, value.volume)) : DEFAULT_AUDIO.volume,
    };
  } catch { return { ...DEFAULT_AUDIO }; }
}

/** Advance the cursor even when muted. The first fresh snapshot after a reconnect is a baseline. */
export class GameAudioCursor {
  private gameId?: string;
  private eventId = -1;
  private throwPhase?: string;
  private resync = false;
  private pausedSnapshot?: GameView;

  observe(game: GameView | undefined, playerId?: string, live = true, audible = true): AudioCue[] {
    if (!live) { this.resync = true; this.pausedSnapshot = game; return []; }
    if (!game) { this.gameId = undefined; this.eventId = -1; this.throwPhase = undefined; return []; }
    if (this.resync && game === this.pausedSnapshot) return [];
    const throwPhase = game.phase === 'throw' && game.throwerId === playerId ? `${game.id}:${game.phaseId}` : undefined;
    const latest = Math.max(-1, ...game.events.map(event => event.id));
    if (this.resync || this.gameId !== game.id) {
      this.resync = false; this.pausedSnapshot = undefined; this.gameId = game.id;
      this.eventId = latest; this.throwPhase = throwPhase;
      return [];
    }
    const fresh = game.events.filter(event => event.id > this.eventId).sort((a, b) => a.id - b.id);
    this.eventId = Math.max(this.eventId, latest);
    const cues: AudioCue[] = [];
    for (const event of fresh) {
      if (event.type === 'throw' || event.type === 'move' || event.type === 'capture' || event.type === 'stack') cues.push(event.type);
      else if (event.type === 'finish') cues.push(game.winner ? 'victory' : 'finish');
      else if (event.type === 'card' || event.type === 'environment') cues.push(event.type);
    }
    if (throwPhase && throwPhase !== this.throwPhase) cues.push('your-turn');
    this.throwPhase = throwPhase;
    // Compound moves keep every distinct cue; one final victory fanfare covers two finish events.
    return audible ? [...new Set(cues)].slice(-6) : [];
  }
}

type Bus = 'music' | 'effects';
type Voice = { source: AudioScheduledSourceNode; bus: Bus; nodes: AudioNode[] };
type AudioContextConstructor = new (options?: AudioContextOptions) => AudioContext;
const noteFrequency = (midi: number) => 440 * 2 ** ((midi - 69) / 12);
const EIGHTH_SECONDS = 60 / 78 / 2;

// Original sixteen-bar D-pentatonic melody. Rests and two answering phrases give the loop breathing room.
const MELODY: readonly (readonly (number | null)[])[] = [
  [66, null, 69, 74, null, 71, 69, null], [64, null, 66, null, 69, null, null, null],
  [62, null, 66, 69, null, 66, 64, null], [64, null, 62, null, null, null, 64, null],
  [66, null, 69, null, 71, 69, 66, null], [64, null, 66, 69, null, 66, null, null],
  [62, null, 64, 66, null, 69, 66, null], [64, null, 62, null, null, null, null, null],
  [74, null, 71, 69, null, 66, 69, null], [71, null, 69, null, 66, null, null, null],
  [69, null, 66, 64, null, 62, 66, null], [64, null, 66, null, null, null, 69, null],
  [71, null, 74, 71, null, 69, 66, null], [69, null, 66, 64, null, 66, null, null],
  [62, null, 66, 69, null, 66, 64, null], [62, null, null, null, null, null, null, null],
];
const HARMONY = [[50, 57, 66], [47, 54, 62], [43, 50, 59], [45, 52, 61]] as const;

/** Browser-only original synthesis: no recordings, remote files, libraries, or continuing hidden-tab timers. */
export class PartyAudio {
  private context?: AudioContext;
  private master?: GainNode;
  private music?: GainNode;
  private effects?: GainNode;
  private compressor?: DynamicsCompressorNode;
  private noise?: AudioBuffer;
  private voices = new Set<Voice>();
  private timer?: ReturnType<typeof setInterval>;
  private preferences: AudioPreferences = { ...DEFAULT_AUDIO };
  private step = 0;
  private nextBeat = 0;
  private visible = true;
  private disposed = false;
  private gesture = false;
  private activation?: Promise<boolean>;

  constructor(private onStatus: (status: AudioStatus) => void = () => undefined) {}

  configure(preferences: AudioPreferences): void {
    this.preferences = { ...preferences };
    if (this.master && this.context) this.master.gain.setTargetAtTime(preferences.volume, this.context.currentTime, 0.03);
    if (!preferences.sfx) this.stopVoices('effects');
    if (!preferences.bgm) this.stopMusic(); else this.startMusic();
    if (!preferences.bgm && !preferences.sfx && this.context?.state === 'running') void this.context.suspend().catch(() => undefined);
  }

  /** Call directly from a trusted pointer/keyboard handler, before awaiting other work. */
  unlock(): Promise<boolean> {
    if (this.disposed || !this.visible || (!this.preferences.bgm && !this.preferences.sfx)) return Promise.resolve(false);
    if (this.activation) return this.activation;
    this.gesture = true;
    try {
      if (!this.context) this.createContext();
      if (!this.context) return Promise.resolve(false);
      const context = this.context;
      const resumed = context.state === 'running' ? Promise.resolve() : context.resume();
      this.activation = resumed.then(() => {
        if (this.disposed) return false;
        if (!this.visible || (!this.preferences.bgm && !this.preferences.sfx)) {
          if (context.state === 'running') void context.suspend().catch(() => undefined);
          return false;
        }
        if (context.state !== 'running') return false;
        this.onStatus('ready'); this.startMusic(); return true;
      }).catch(() => { if (!this.disposed) this.onStatus('locked'); return false; }).finally(() => { this.activation = undefined; });
      return this.activation;
    } catch { this.onStatus('unsupported'); return Promise.resolve(false); }
  }

  setVisible(visible: boolean): void {
    if (this.visible === visible || this.disposed) return;
    this.visible = visible;
    if (!visible) {
      this.stopMusic(); this.stopVoices();
      if (this.context?.state === 'running') void this.context.suspend().catch(() => undefined);
      this.onStatus('paused');
    } else if (this.gesture) void this.unlock();
    else this.onStatus('locked');
  }

  play(cues: readonly AudioCue[]): void {
    const context = this.context;
    if (this.disposed || !this.visible || !this.preferences.sfx || !context || context.state !== 'running') return;
    let at = context.currentTime + 0.018;
    for (const cue of cues.slice(-6)) {
      this.effect(cue, at);
      at += cue === 'throw' ? 0.26 : cue === 'move' ? 0.1 : 0.16;
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true; this.stopMusic(); this.stopVoices();
    if (this.context) { this.context.onstatechange = null; void this.context.close().catch(() => undefined); }
    this.master?.disconnect(); this.music?.disconnect(); this.effects?.disconnect(); this.compressor?.disconnect();
    this.context = undefined; this.noise = undefined;
  }

  private createContext(): void {
    if (typeof window === 'undefined') return;
    const Constructor = window.AudioContext || (window as unknown as { webkitAudioContext?: AudioContextConstructor }).webkitAudioContext;
    if (!Constructor) { this.onStatus('unsupported'); return; }
    const context = new Constructor({ latencyHint: 'interactive' });
    this.context = context;
    this.master = context.createGain(); this.master.gain.value = this.preferences.volume;
    this.music = context.createGain(); this.music.gain.value = 0.38;
    this.effects = context.createGain(); this.effects.gain.value = 0.78;
    this.compressor = context.createDynamicsCompressor();
    this.compressor.threshold.value = -18; this.compressor.knee.value = 18; this.compressor.ratio.value = 3;
    this.compressor.attack.value = 0.008; this.compressor.release.value = 0.2;
    this.music.connect(this.master); this.effects.connect(this.master);
    this.master.connect(this.compressor); this.compressor.connect(context.destination);
    this.noise = context.createBuffer(1, Math.ceil(context.sampleRate * 0.12), context.sampleRate);
    const data = this.noise.getChannelData(0);
    let seed = 19771011;
    for (let index = 0; index < data.length; index++) { seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0; data[index] = (seed / 0x100000000) * 2 - 1; }
    context.onstatechange = () => {
      if (this.disposed) return;
      if (context.state === 'running' && this.visible) { this.onStatus('ready'); this.startMusic(); }
      else { this.stopMusic(); this.onStatus(this.visible ? 'locked' : 'paused'); }
    };
  }

  private startMusic(): void {
    if (this.timer !== undefined || this.disposed || !this.visible || !this.preferences.bgm || this.context?.state !== 'running') return;
    this.nextBeat = this.context.currentTime + 0.05;
    this.scheduleMusic();
    this.timer = setInterval(() => this.scheduleMusic(), 75);
  }
  private stopMusic(): void {
    if (this.timer !== undefined) clearInterval(this.timer);
    this.timer = undefined; this.stopVoices('music');
  }
  private scheduleMusic(): void {
    const context = this.context;
    if (!context || this.disposed || !this.visible || !this.preferences.bgm || context.state !== 'running') return;
    // A throttled tab never catches up with a burst of notes.
    if (this.nextBeat < context.currentTime - EIGHTH_SECONDS) this.nextBeat = context.currentTime + 0.04;
    while (this.nextBeat < context.currentTime + 0.18) {
      const bar = Math.floor(this.step / 8) % MELODY.length;
      const beat = this.step % 8;
      const midi = MELODY[bar]![beat];
      const chord = HARMONY[bar % HARMONY.length]!;
      if (midi !== null && midi !== undefined) this.marimba(midi, this.nextBeat + (beat % 2 ? 0.014 : 0), 0.12, 'music', bar % 2 ? 0.14 : -0.14);
      if (beat === 0 || beat === 4) this.tone(noteFrequency(chord[0] - 12), this.nextBeat, 1.1, beat === 0 ? 0.085 : 0.06, 'music', 0.018);
      if (beat === 0) for (const midi of chord.slice(1)) this.tone(noteFrequency(midi), this.nextBeat, 2.5, 0.026, 'music', 0.3);
      if (beat === 2 || beat === 6) this.wood(this.nextBeat, beat === 2 ? 660 : 540, 0.02, 'music');
      this.step = (this.step + 1) % (MELODY.length * 8);
      this.nextBeat += EIGHTH_SECONDS;
    }
  }

  private effect(cue: AudioCue, at: number): void {
    if (cue === 'throw') {
      // Hand rattle, four staggered floor impacts, then the lighter second bounce.
      [0, 0.035, 0.07].forEach((offset, index) => this.wood(at + offset, 1000 - index * 110, 0.045, 'effects'));
      [0.79, 0.822, 0.854, 0.886].forEach((offset, index) => {
        this.wood(at + offset, [770, 560, 910, 620][index]!, 0.29 - index * 0.02, 'effects');
        this.wood(at + 1.13 + index * 0.032, [690, 510, 840, 590][index]!, 0.09, 'effects');
        this.wood(at + 1.35 + index * 0.02, 810, 0.025, 'effects');
      });
    } else if (cue === 'card') {
      this.wood(at, 1200, 0.09, 'effects');
      [69, 74].forEach((note, index) => this.marimba(note, at + .04 + index * .06, .09, 'effects'));
    } else if (cue === 'environment') {
      this.wood(at, 240, .19, 'effects');
      this.tone(70, at, .3, .11, 'effects', .008);
      this.marimba(57, at + .12, .1, 'effects');
    } else if (cue === 'move') {
      this.wood(at, 830, 0.065, 'effects'); this.wood(at + 0.88, 510, 0.15, 'effects');
    } else if (cue === 'stack') {
      this.wood(at + 0.78, 710, 0.13, 'effects');
      [62, 66, 69].forEach((note, index) => this.marimba(note, at + 0.82 + index * 0.07, 0.13, 'effects'));
    } else if (cue === 'capture') {
      this.wood(at + 0.79, 270, 0.3, 'effects');
      this.tone(90, at + 0.79, .18, .15, 'effects', .003);
      [57, 62, 69].forEach((note, index) => this.marimba(note, at + 0.88 + index * 0.07, 0.15, 'effects'));
    } else if (cue === 'finish') {
      [66, 69, 74].forEach((note, index) => this.marimba(note, at + index * 0.105, 0.15, 'effects'));
    } else if (cue === 'victory') {
      [62, 66, 69, 74, 71, 74].forEach((note, index) => this.marimba(note, at + index * 0.13, index === 5 ? 0.19 : 0.145, 'effects'));
      [50, 57, 66].forEach(note => this.tone(noteFrequency(note), at + 0.39, 1.3, 0.052, 'effects', 0.1));
    } else {
      this.marimba(69, at, 0.1, 'effects'); this.marimba(74, at + 0.17, 0.13, 'effects');
    }
  }
  private marimba(midi: number, at: number, level: number, bus: Bus, pan = 0): void {
    const frequency = noteFrequency(midi);
    this.tone(frequency, at, 0.76, level, bus, 0.007, pan);
    this.tone(frequency * 2.76, at, 0.105, level * 0.15, bus, 0.003, pan);
    this.tone(frequency * 4, at, 0.06, level * 0.035, bus, 0.003, pan);
  }
  private tone(frequency: number, at: number, duration: number, level: number, bus: Bus, attack = 0.008, pan = 0): void {
    const context = this.context;
    if (!context) return;
    const oscillator = context.createOscillator(); const gain = context.createGain();
    const panner = context.createStereoPanner();
    oscillator.type = 'sine'; oscillator.frequency.setValueAtTime(frequency, at);
    gain.gain.setValueAtTime(0, at); gain.gain.linearRampToValueAtTime(level, at + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + duration); gain.gain.linearRampToValueAtTime(0, at + duration + 0.015);
    panner.pan.value = pan;
    oscillator.connect(gain); gain.connect(panner); panner.connect(bus === 'music' ? this.music! : this.effects!);
    this.track(oscillator, bus, [oscillator, gain, panner]);
    oscillator.start(at); oscillator.stop(at + duration + 0.02);
  }
  private wood(at: number, frequency: number, level: number, bus: Bus): void {
    const context = this.context;
    if (!context || !this.noise) return;
    const source = context.createBufferSource(); const filter = context.createBiquadFilter(); const gain = context.createGain();
    source.buffer = this.noise; filter.type = 'bandpass'; filter.frequency.value = frequency; filter.Q.value = 4.8;
    gain.gain.setValueAtTime(0, at); gain.gain.linearRampToValueAtTime(level * 1.4, at + 0.002);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.075);
    source.connect(filter); filter.connect(gain); gain.connect(bus === 'music' ? this.music! : this.effects!);
    this.track(source, bus, [source, filter, gain]); source.start(at); source.stop(at + 0.085);
    this.tone(frequency * 0.63, at, 0.085, level * 0.24, bus, 0.002);
  }
  private track(source: AudioScheduledSourceNode, bus: Bus, nodes: AudioNode[]): void {
    const voice = { source, bus, nodes };
    this.voices.add(voice);
    source.onended = () => { nodes.forEach(node => node.disconnect()); this.voices.delete(voice); };
  }
  private stopVoices(bus?: Bus): void {
    for (const voice of this.voices) {
      if (bus && voice.bus !== bus) continue;
      voice.source.onended = null;
      try { voice.source.stop(); } catch { /* The scheduled voice may already have ended. */ }
      voice.nodes.forEach(node => node.disconnect()); this.voices.delete(voice);
    }
  }
}
